// docs/34 SX11 — the storm's schedule, shared by every player without a message: time is cut into
// slots of the storm's `flashEvery`; slot n of encounter E strikes (or not) at a moment, at a
// distance and with a thunderclap variant that are all a hash of (E, n). Read against the server
// clock (core/time.ts), every client flashes the same lightning and hears the same thunder, the
// flash first and the clap a distance later. Weather is the room's (a party sees one sky, docs/20),
// so this is as much the server's choice as a cue row — at no cost per strike.
import { hash32, seeded } from './mix';

export interface Strike {
  /** Server ms of the flash. */
  at: number;
  /** 0 overhead .. 1 far off: the clap's delay, level and pitch. */
  distance: number;
  /** Picks the thunder's variant (`seed % variants`). */
  seed: number;
}

/** The strike of slot `slot`, if that slot has one (about seven in eight do). */
export function strikeIn(key: string, every: number, slot: number): Strike | undefined {
  const r = seeded(hash32(`${key}|storm|${slot}`));
  if (r() < 0.12) return undefined;
  const at = slot * every + every * (0.1 + 0.8 * r());
  return { at, distance: r(), seed: Math.floor(r() * 2 ** 31) };
}

/** The first strike after server time `after` (scans forward a few slots). */
export function nextStrike(key: string, every: number, after: number): Strike | undefined {
  if (every <= 0) return undefined;
  const first = Math.floor(after / every);
  for (let s = first; s < first + 8; s++) { const st = strikeIn(key, every, s); if (st && st.at > after) return st; }
  return undefined;
}

/** How long after the flash its thunder is heard (ms), how loud (×) and at what rate. */
export function thunderOf(s: Strike): { delayMs: number; gain: number; rate: number } {
  return { delayMs: 180 + s.distance * 2600, gain: 1 - 0.55 * s.distance, rate: 1 - 0.12 * s.distance };
}

/** The storm on the board now (set by the ambience controller, read by the weather layer's flashes). */
let current: { key: string; every: number } | undefined;
export const setStorm = (s: { key: string; every: number } | undefined): void => { current = s; };
export const storm = (): { key: string; every: number } | undefined => current;
